import { KalturaSubscription } from '@kaltura-ott/ott-client-types';
import { Observable, of } from 'rxjs';

const CACHE_TTL = 15 * 60 * 1000;

type SubscriptionFilter = {
  subscriptionIds?: string[];
  mediaFileId?: number;
};

const DEFAULT_CACHE = {
  subscriptionList: {},
  subscription: {},
};

const subscriptionCache: {
  subscriptionList: {
    [id: string]: {
      value: KalturaSubscription;
      expiry: number;
    };
  };
  subscription: {
    [mediaFileId: number]: {
      value: string[];
      expiry: number;
    };
  };
} = DEFAULT_CACHE;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const saveToCache = (cache: any, key: number | string, value: any) => {
  cache[key] = {
    value,
    expiry: Date.now() + CACHE_TTL,
  };
};

export const setSubscriptionCache = (subscriptionFilter: SubscriptionFilter, subscriptions: KalturaSubscription[]) => {
  if (subscriptionFilter.mediaFileId) {
    saveToCache(
      subscriptionCache.subscription,
      subscriptionFilter.mediaFileId,
      subscriptions.map((it) => it.id),
    );
    subscriptions.forEach(async (item) => {
      saveToCache(subscriptionCache.subscriptionList, item.id, item);
    });
  } else if (subscriptionFilter.subscriptionIds) {
    subscriptions.forEach(async (item) => {
      saveToCache(subscriptionCache.subscriptionList, item.id, item);
    });
  }
};

const getSubscriptionsFromCacheByIds = (subscriptionIds: string[]): KalturaSubscription[] | null => {
  const cachedSubscriptions = subscriptionIds?.map((id) => {
    const subscriptionListCacheValue = subscriptionCache.subscriptionList[id];

    if (subscriptionListCacheValue?.expiry < Date.now()) {
      delete subscriptionCache.subscriptionList[id];
      return null;
    }

    return subscriptionListCacheValue?.value;
  });

  if (cachedSubscriptions.some((it) => !it)) {
    return null;
  }

  return cachedSubscriptions as KalturaSubscription[];
};

export const getSubscriptionCache = (
  subscriptionFilter: SubscriptionFilter,
): Observable<KalturaSubscription[] | null> => {
  if (subscriptionFilter.mediaFileId) {
    const subscriptionCacheValue = subscriptionCache.subscription[subscriptionFilter.mediaFileId];
    const subscriptionIds = subscriptionCacheValue?.value;

    if (!subscriptionIds) {
      return of(null);
    }

    if (subscriptionCacheValue?.expiry < Date.now()) {
      delete subscriptionCache.subscription[subscriptionFilter.mediaFileId];
      return of(null);
    }

    return of(getSubscriptionsFromCacheByIds(subscriptionIds));
  } else if (subscriptionFilter.subscriptionIds) {
    return of(getSubscriptionsFromCacheByIds(subscriptionFilter.subscriptionIds));
  }

  return of(null);
};
